import React, { useEffect, useState } from 'react';
import { Prospect } from '../../shared/types';
import { arquiteturaProjetos, ArchProject } from '../../shared/data/arquiteturaData';
import { Compass, Layers, Eye, Calculator, ChevronRight, Phone, MapPin, CheckCircle2, ArrowRight } from 'lucide-react';

interface ArchDemoProps {
  prospect: Prospect;
}

export const ArchDemo: React.FC<ArchDemoProps> = ({ prospect }) => {
  const [activeCategory, setActiveCategory] = useState<'todos' | 'residencial' | 'comercial' | 'interiores'>('todos');
  const [showBefore, setShowBefore] = useState<Record<string, boolean>>({});
  const [areaInput, setAreaInput] = useState<number>(150);
  const [standardInput, setStandardInput] = useState<'alto' | 'luxo'>('luxo');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredProjects = activeCategory === 'todos' 
    ? arquiteturaProjetos 
    : arquiteturaProjetos.filter(p => p.category === activeCategory);

  const toggleBeforeAfter = (id: string) => {
    setShowBefore(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const estimatedCost = areaInput * (standardInput === 'luxo' ? 4800 : 3500);

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1A1A1A] font-sans selection:bg-[#1A1A1A] selection:text-white">
      
      {/* Navbar Architectural Digest - STRICT NO SHADOWS */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FAF9F6]/95 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-8 h-24 flex items-center justify-between">
          <div className="flex items-center gap-4">
             <div className="w-11 h-11 rounded-xl bg-[#1A1A1A] text-[#D4C5B9] flex items-center justify-center font-bold border border-stone-800">
                <Compass className="w-6 h-6" />
             </div>
             <div>
                <h1 className="text-xl font-heading font-bold text-[#1A1A1A] tracking-tight uppercase leading-none">{prospect.trade_name}</h1>
                <span className="text-[10px] text-stone-500 font-medium tracking-widest uppercase">Estúdio de Arquitetura Autoral</span>
             </div>
          </div>

          <nav className="hidden md:flex items-center gap-10 text-xs font-bold uppercase tracking-wider text-stone-600">
             <a href="#portfolio" className="hover:text-[#1A1A1A] transition-colors">Portfólio</a>
             <a href="#antes-depois" className="hover:text-[#1A1A1A] transition-colors">Antes & Depois</a>
             <a href="#calculadora" className="hover:text-[#1A1A1A] transition-colors">Estimador m²</a>
          </nav>

          <button className="hidden sm:flex bg-[#1A1A1A] text-white border border-[#1A1A1A] px-6 py-3 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-stone-800 transition-colors items-center gap-2">
             <Phone className="w-4 h-4 text-[#D4C5B9]" />
             <span>Agendar Briefing</span>
          </button>
        </div>
      </header>

      {/* Hero Minimalista - STRICT NO SHADOWS */}
      <section className="pt-40 pb-20 px-8 border-b border-stone-200 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
           <div className="lg:col-span-7 space-y-6">
              <span className="px-3.5 py-1.5 rounded-xl bg-stone-200/60 border border-stone-300 text-xs font-bold text-stone-700 uppercase tracking-widest inline-block">
                 Prêmio Nacional de Arquitetura 2025
              </span>
              <h2 className="text-5xl lg:text-7xl font-heading font-normal text-[#1A1A1A] tracking-tight leading-[1.08]">
                 Arquitetura que transforma o <br />
                 <span className="italic font-serif text-[#C86D51]">modo de viver.</span>
              </h2>
              <p className="text-base text-stone-600 font-light max-w-xl leading-relaxed">
                 Projetos residenciais e interiores de alto padrão que harmonizam luz natural, materiais nobres e funcionalidade contemporânea.
              </p>

              <div className="pt-4 flex flex-wrap gap-4">
                 <a href="#portfolio" className="bg-[#1A1A1A] text-white px-8 py-4 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-stone-800 transition-colors inline-flex items-center gap-3">
                    Explorar Obras Concluídas <ArrowRight className="w-4 h-4 text-[#D4C5B9]" />
                 </a>
              </div>
           </div>

           <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden border border-stone-300 p-2 bg-white">
                 <div className="aspect-[4/5] rounded-xl overflow-hidden">
                    <img 
                      src={arquiteturaProjetos[0].imageAfter} 
                      alt="Projeto de Arquitetura" 
                      className="w-full h-full object-cover"
                    />
                 </div>
                 <div className="p-4 bg-[#FAF9F6] border border-stone-200 rounded-xl mt-2 flex items-center justify-between">
                    <div>
                       <p className="text-xs font-bold text-[#1A1A1A]">{arquiteturaProjetos[0].title}</p>
                       <p className="text-[10px] text-stone-500">{arquiteturaProjetos[0].location} • {arquiteturaProjetos[0].area}</p>
                    </div>
                    <span className="text-xs font-bold text-[#C86D51] uppercase">{arquiteturaProjetos[0].year}</span>
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* Portfólio com Filtro por Categoria - STRICT NO SHADOWS */}
      <section id="portfolio" className="py-24 px-8 max-w-7xl mx-auto">
         <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 pb-6 border-b border-stone-200">
            <div>
               <span className="text-xs font-bold text-[#C86D51] uppercase tracking-widest">Obras Autorais</span>
               <h3 className="text-3xl font-heading font-bold text-[#1A1A1A] mt-1">Portfólio Selecionado</h3>
            </div>

            {/* Filtros */}
            <div className="flex gap-2 mt-4 md:mt-0">
               {[
                  { id: 'todos', label: 'Todos os Projetos' },
                  { id: 'residencial', label: 'Residencial' },
                  { id: 'interiores', label: 'Interiores' },
                  { id: 'comercial', label: 'Comercial' },
               ].map(cat => (
                  <button
                     key={cat.id}
                     onClick={() => setActiveCategory(cat.id as any)}
                     className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors border ${
                        activeCategory === cat.id 
                           ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' 
                           : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'
                     }`}
                  >
                     {cat.label}
                  </button>
               ))}
            </div>
         </div>

         <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {filteredProjects.map((proj) => (
               <div key={proj.id} className="bg-white rounded-2xl border border-stone-200 overflow-hidden hover:border-stone-400 transition-all flex flex-col justify-between p-3">
                  <div>
                     <div className="relative aspect-[16/10] rounded-xl overflow-hidden mb-4 border border-stone-200">
                        <img 
                           src={showBefore[proj.id] && proj.imageBefore ? proj.imageBefore : proj.imageAfter} 
                           alt={proj.title} 
                           className="w-full h-full object-cover"
                        />
                        {proj.imageBefore && (
                           <button 
                              onClick={() => toggleBeforeAfter(proj.id)}
                              className="absolute top-4 right-4 bg-[#1A1A1A] text-white px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 border border-stone-700 hover:bg-stone-800"
                           >
                              <Eye className="w-3.5 h-3.5 text-[#D4C5B9]" /> 
                              {showBefore[proj.id] ? 'Ver Depois (Projeto Concluído)' : 'Ver Antes (Estado Original)'}
                           </button>
                        )}
                     </div>
                     <div className="px-3">
                        <span className="text-[10px] font-bold text-[#C86D51] uppercase tracking-widest">{proj.category} • {proj.area}</span>
                        <h4 className="font-heading font-bold text-2xl text-[#1A1A1A] mb-2">{proj.title}</h4>
                        <p className="text-xs text-stone-600 font-light leading-relaxed mb-4">{proj.description}</p>
                     </div>
                  </div>

                  <div className="px-3 pb-3 pt-3 border-t border-stone-100 flex items-center justify-between text-xs text-stone-500 font-medium">
                     <span>{proj.location}</span>
                     <span className="font-bold text-[#1A1A1A] uppercase tracking-wider flex items-center gap-1">Ver Prancha Técnica <ChevronRight className="w-4 h-4" /></span>
                  </div>
               </div>
            ))}
         </div>
      </section>

      {/* Calculadora de Estimativa de Investimento por m² - STRICT NO SHADOWS */}
      <section id="calculadora" className="py-20 px-8 bg-stone-100 border-t border-stone-200">
         <div className="max-w-4xl mx-auto bg-white p-10 rounded-2xl border border-stone-300">
            <div className="text-center mb-8">
               <span className="text-xs font-bold uppercase tracking-widest text-[#C86D51] flex items-center justify-center gap-2">
                  <Calculator className="w-4 h-4" /> Planejamento Financeiro
               </span>
               <h3 className="text-3xl font-heading font-bold text-[#1A1A1A] mt-2">Estimador de Investimento em Obra</h3>
               <p className="text-xs text-stone-500 font-light mt-1">Calcule a estimativa preliminar para projeto de arquitetura + execução de obra de alto padrão.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
               <div>
                  <label className="text-xs font-bold text-stone-700 block mb-2">Área Estimada (m²): {areaInput} m²</label>
                  <input 
                     type="range" 
                     min="50" 
                     max="1000" 
                     step="10" 
                     value={areaInput} 
                     onChange={e => setAreaInput(Number(e.target.value))} 
                     className="w-full accent-[#1A1A1A]" 
                  />
               </div>

               <div>
                  <label className="text-xs font-bold text-stone-700 block mb-2">Padrão de Acabamento</label>
                  <select 
                     value={standardInput} 
                     onChange={e => setStandardInput(e.target.value as any)} 
                     className="w-full bg-[#FAF9F6] border border-stone-300 rounded-xl p-3 text-xs font-bold text-[#1A1A1A] outline-none"
                  >
                     <option value="alto">Alto Padrão (Mármores Nacionais + Iluminação LED)</option>
                     <option value="luxo">Super Luxo (Importados, Automação Savant, Marcenaria Prime)</option>
                  </select>
               </div>
            </div>

            <div className="bg-[#FAF9F6] p-6 rounded-xl border border-stone-300 flex flex-col md:flex-row items-center justify-between gap-4">
               <div>
                  <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest block">Estimativa Geral de Projeto + Obra</span>
                  <span className="text-3xl font-heading font-bold text-[#1A1A1A]">R$ {estimatedCost.toLocaleString('pt-BR')},00</span>
               </div>
               <button className="bg-[#1A1A1A] text-white font-bold text-xs uppercase tracking-wider px-6 py-3.5 rounded-xl hover:bg-stone-800 transition-colors border border-stone-800">
                  Solicitar Proposta Executiva com o Estúdio
               </button>
            </div>
         </div>
      </section>

      {/* Footer Minimalista */}
      <footer className="bg-[#1A1A1A] text-stone-400 py-12 px-8 border-t border-stone-800 text-xs">
         <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
               <Compass className="w-5 h-5 text-[#D4C5B9]" />
               <span className="font-bold text-white text-sm">{prospect.trade_name}</span>
            </div>
            <span>Alameda Gabriel Monteiro da Silva, 1890 - São Paulo</span>
            <span>&copy; {new Date().getFullYear()} YouMachine Architectural Engine.</span>
         </div>
      </footer>

    </div>
  );
};
